import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import {
  useDeleteBusinessAccountMutation,
  useDeleteOrganizationMutation,
  usePutLocationMutation
} from '../../../../services/device/deviceService';
import { selectCurrentUser } from '../../../../store/slices/login/login';
import { viewRightSideBarEnum } from '../../../../constants/sidebarEnums';
import {
  useDeleteLocationByRoleMutation,
  useResetLocationMutation
} from '../../../../services/locations/locationsService';
import {
  StyledContainerFooterSideBar,
  StyledContainerItems,
  StyledTitleSideBar
} from '../../../../components/commons/SideBarComponents/sideBarComponentsStyles';
import { SideBarSimpleMethodItem } from '../../../../components/commons/SideBarComponents/SimpleMethodItem/sideBarSimpleMethodButtonItem';
import { SideBarReadOnly } from '../../../../components/commons/SideBarComponents/ReadOnlyItems/sideBarReadOnly';
import { SideBarEditablePropertyItem } from '../../../../components/commons/SideBarComponents/EditablePropertyItem/sideBarEditablePropertyItem';
import { SideBarDeleteActionWithPopup } from '../../../../components/commons/SideBarComponents/DeleteActionWithPopup/sideBarDeleteActionWithPopup';
import Strings from '../../../../i18n';
import { ILocOrgItem } from '../../../../types/location/location';
import { homeListTypes } from '../../../../constants/commonEnums';
import { SideBarResetActionWithPopup } from '../../../../components/commons/SideBarComponents/ResetActionWithPopup/sideBarResetActionWithPopup';

interface locationListByUserDetailsProps {
  locationOrg: ILocOrgItem;
  reloadTable: () => void;
  openRightSideBarForMethod: (method: number) => void;
  setOpenRightSideBar: (value: boolean) => void;
  ownerId?: number;
}

export const LocationListByUserDetails = ({
  reloadTable,
  locationOrg,
  openRightSideBarForMethod,
  setOpenRightSideBar,
  ownerId = undefined
}: locationListByUserDetailsProps) => {
  const [name, setName] = useState('');
  const [address, setAddress] = useState<string | undefined>('');
  const isLocation = locationOrg.type === homeListTypes.location;
  const navigate = useNavigate();
  const isBusinessAccount = locationOrg.type === homeListTypes.businessAccount;
  const isOrganization = locationOrg.type === homeListTypes.organization;
  const contextUser = useSelector(selectCurrentUser);
  const [editLocation, { isLoading, isSuccess }] = usePutLocationMutation();

  const [
    deleteLocationByRole,
    {
      isSuccess: isSuccessDeleteLocationByRole,
      isError: isErrorDeleteLocationByRole,
      error: errorDeleteLocationByRole
    }
  ] = useDeleteLocationByRoleMutation();

  useEffect(() => {
    if (isErrorDeleteLocationByRole && errorDeleteLocationByRole) {
      if (
        'status' in errorDeleteLocationByRole &&
        'data' in errorDeleteLocationByRole
      ) {
        const errorData = errorDeleteLocationByRole.data as {
          message?: string;
        };
        toast.error(errorData.message || Strings.common.somethingWentWrong);
      } else {
        toast.error(Strings.common.somethingWentWrong);
      }
    }
  }, [isErrorDeleteLocationByRole, errorDeleteLocationByRole]);

  const [deleteOrganization, { isSuccess: isSuccessDeleteOrganization }] =
    useDeleteOrganizationMutation();

  const [deleteBusinessA, { isSuccess: isSuccessDeleteBusiness }] =
    useDeleteBusinessAccountMutation();

  const [resetLocation, { isSuccess: isSuccessResetLocation }] =
    useResetLocationMutation();

  useEffect(() => {
    if (isSuccessResetLocation) {
      toast.success(Strings.locations.resetLocationSuccess);
      setOpenRightSideBar(false);
      reloadTable();
    }
  }, [isSuccessResetLocation]);

  useEffect(() => {
    if (locationOrg) {
      setName(locationOrg.name);

      setAddress(locationOrg.address);
    }
  }, [locationOrg]);

  useEffect(() => {
    if (isSuccess) {
      toast.success(Strings.locations.successEditLocation);
      setOpenRightSideBar(false);
      reloadTable();
    }
  }, [isSuccess]);

  const deleteLocationOrganization = () => {
    if (isLocation) {
      const data = {
        locationId: locationOrg.id,
        userId: ownerId || locationOrg.ownerId
      };
      deleteLocationByRole([data]);
    } else {
      deleteOrganization([locationOrg.id]);
    }
  };

  const redirectToViewMembers = () => {
    navigate(`/business-account/${locationOrg.id}/users`);
  };

  const deleteBusiness = () => {
    deleteBusinessA(locationOrg.id.toString());
  };

  useEffect(() => {
    if (isSuccessDeleteLocationByRole) {
      toast.success(Strings.locations.successDeleteLocation);
      setOpenRightSideBar(false);
      reloadTable();
    }
  }, [isSuccessDeleteLocationByRole]);
  useEffect(() => {
    if (isSuccessDeleteOrganization) {
      toast.success(Strings.organizations.deleteOrganization);
      setOpenRightSideBar(false);
      reloadTable();
    }
  }, [isSuccessDeleteOrganization]);

  useEffect(() => {
    if (isSuccessDeleteBusiness) {
      toast.success(Strings.businessAccount.deleteBusiness);
      setOpenRightSideBar(false);
      reloadTable();
    }
  }, [isSuccessDeleteBusiness]);

  const handleResetLocation = () => {
    if (isLocation) {
      resetLocation([locationOrg.id.toString()]);
    }
  };

  return (
    <>
      <StyledContainerItems>
        <StyledTitleSideBar>{locationOrg.name}</StyledTitleSideBar>
        {!isBusinessAccount && (
          <SideBarSimpleMethodItem
            name={Strings.locations.transferOwnership}
            action={() => {
              openRightSideBarForMethod(viewRightSideBarEnum.transferLocation);
            }}
            successAction={false}
            textSuccess="success"
          />
        )}
        {isOrganization && (
          <SideBarSimpleMethodItem
            name={
              isLocation
                ? Strings.locations.shareLocation
                : Strings.organizations.shareOrg
            }
            action={() => {
              openRightSideBarForMethod(viewRightSideBarEnum.shareLocation);
            }}
            successAction={false}
            textSuccess="success"
          />
        )}
        {isLocation && (
          <SideBarSimpleMethodItem
            name={Strings.serverAccess.devices.deferOnOffMethod}
            action={() => {
              openRightSideBarForMethod(viewRightSideBarEnum.deferOnOffMethod);
            }}
            successAction={false}
            textSuccess="success"
          />
        )}
        {isLocation && (
          <SideBarSimpleMethodItem
            name={Strings.locations.shareLocationsWithTargets}
            action={() => {
              openRightSideBarForMethod(
                viewRightSideBarEnum.shareLocationsWithTargets
              );
            }}
            successAction={false}
            textSuccess="success"
          />
        )}
        {isLocation && (
          <SideBarSimpleMethodItem
            name={Strings.locations.changeLocationType}
            action={() => {
              openRightSideBarForMethod(
                viewRightSideBarEnum.changeLocationType
              );
            }}
            successAction={false}
            textSuccess="success"
          />
        )}
        {isLocation && (
          <SideBarSimpleMethodItem
            name={Strings.locations.moveToOrg}
            action={() => {
              openRightSideBarForMethod(viewRightSideBarEnum.moveLocation);
            }}
            successAction={false}
            textSuccess="success"
          />
        )}
        {isLocation && (
          <SideBarSimpleMethodItem
            name={Strings.notes.addNote}
            action={() => {
              openRightSideBarForMethod(viewRightSideBarEnum.addNote);
            }}
            successAction={false}
            textSuccess="success"
          />
        )}
        {isLocation && (
          <SideBarResetActionWithPopup
            onReset={handleResetLocation}
            titleConfirmation={Strings.locations.resetLocation}
            textConfirmation={Strings.common.thisActionIsIrreversible}
            textIcon={Strings.locations.resetLocation}
          />
        )}
        {isLocation && (
          <SideBarSimpleMethodItem
            name={Strings.locations.removeLocation}
            action={() => {
              const data = {
                locationId: locationOrg.id,
                userId: locationOrg.isOwner
                  ? locationOrg.ownerId
                  : (contextUser.userId as number)
              };
              deleteLocationByRole([data]);
            }}
            successAction={false}
            textSuccess="success"
          />
        )}
        <SideBarEditablePropertyItem
          item={{
            label: `${locationOrg.type} name`,
            type: 'inputText',
            currentValue: name,
            placeholder: '',
            disabled: !locationOrg.isOwner,
            onChange: (e) => setName(e.target.value)
          }}
        />
        <SideBarReadOnly
          readOnlyItems={[
            {
              name: Strings.locations.owner,
              value: locationOrg.isOwner
                ? contextUser.email
                : locationOrg.ownerEmail
            }
          ]}
        />
        <SideBarEditablePropertyItem
          item={{
            label: Strings.businessAccount.address,
            type: 'inputText',
            currentValue: address || '',
            placeholder: '',
            disabled: !locationOrg.isOwner,
            onChange: (e) => setAddress(e.target.value)
          }}
        />
        {isLocation && (
          <SideBarReadOnly
            readOnlyItems={[
              {
                name: Strings.locations.apiKey,
                value: locationOrg.accessToken
              }
            ]}
          />
        )}
        {isBusinessAccount && (
          <SideBarSimpleMethodItem
            name={Strings.businessAccount.viewMembers}
            action={() => {
              redirectToViewMembers();
            }}
            successAction={false}
            textSuccess="success"
          />
        )}
        {isBusinessAccount && locationOrg.isOwner && (
          <SideBarDeleteActionWithPopup
            onDelete={() => deleteBusiness()}
            titleConfirmation={Strings.businessAccount.deleteBusiness}
            textConfirmation={Strings.common.thisActionIsIrreversible}
            textIcon={Strings.businessAccount.deleteBusinessAccount}
          />
        )}
        {isOrganization && (
          <SideBarDeleteActionWithPopup
            onDelete={() => deleteLocationOrganization()}
            titleConfirmation={
              isLocation
                ? Strings.locations.deleteLocationTitle
                : Strings.organizations.deleteOrganizationTitle
            }
            textConfirmation={Strings.common.thisActionIsIrreversible}
            textIcon={
              isLocation
                ? Strings.locations.deleteLocation
                : Strings.organizations.deleteOrganization
            }
          />
        )}
      </StyledContainerItems>
      <StyledContainerFooterSideBar>
        <button
          type="button"
          className="btn-cancel"
          onClick={() => {
            setOpenRightSideBar(false);
          }}
        >
          {Strings.common.cancel}
        </button>
        {!isBusinessAccount && (
          <button
            type="button"
            className="btn-save"
            disabled={!name.length || !locationOrg.isOwner || isLoading}
            onClick={() => {
              editLocation({
                id: locationOrg.id,
                Name: name,
                OwnerId: contextUser.userId || 0,
                GeoInfo: {
                  Address: address || '',
                  Latitude: 20.6763989,
                  Longitude: -103.3479102
                }
              });
            }}
          >
            {Strings.common.save}
          </button>
        )}
      </StyledContainerFooterSideBar>
    </>
  );
};
